import React, { Component } from 'react'
import axios from 'axios'

import ContentHeader from '../../common/template/ContentHeader'
import Content from '../../common/template/Content'
import InfoBox from '../../common/layout/widget/InfoBox'
import TwoAxesChart from '../../common/layout/widget/chart/TwoAxesChart'
import { APP_NAME } from '../config'

function dayLabel(iso) {
  if (!iso || iso.length < 10) return iso || ''
  return iso.slice(8, 10)
}

const chartCellStyle = {
  width: '50%',
  marginBottom: 20,
  display: 'inline-block',
  verticalAlign: 'top',
  boxSizing: 'border-box',
  paddingRight: 8,
  paddingLeft: 8,
}

const CHART_HEIGHT = 200

function chartProps(data) {
  return { data, height: CHART_HEIGHT }
}

export default class Dashboard extends Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      error: null,
      stats: null,
      capacityChart: [],
    }
  }

  componentWillMount() {
    document.title = APP_NAME
  }

  componentDidMount() {
    this.load()
  }

  load = async () => {
    this.setState({ loading: true, error: null })
    const api = process.env.REACT_APP_API_HOST || ''
    try {
      const statsRes = await axios.get(`${api}/privzap/dashboard-stats`, { params: { days: 30 } })
      const stats = (statsRes.data && statsRes.data.data) || null
      let capacityChart = []
      try {
        const tlRes = await axios.get(`${api}/privzap/capacity/timeline`, { params: { hours: 24 } })
        const rows = (tlRes.data && (tlRes.data.data || tlRes.data)) || []
        capacityChart = (Array.isArray(rows) ? rows : []).map(r => ({
          argument: String(r.recorded_at || '').slice(11, 16) || String(r.recorded_at),
          active: Number(r.active_count) || 0,
          slots: Number(r.slot_limit) || 0,
        }))
      } catch (_) {
        // timeline is optional
      }
      this.setState({ stats, capacityChart, loading: false })
    } catch (e) {
      const msg = (e.response && e.response.data && e.response.data.message)
        || 'Não foi possível carregar as métricas do dashboard.'
      this.setState({
        loading: false,
        error: msg,
      })
    }
  }

  renderSignupChart(stats) {
    const data = (stats.signups || []).map(r => ({
      argument: dayLabel(r.date),
      signups: Number(r.count) || 0,
    }))
    if (!data.length) return null
    return (
      <TwoAxesChart
        title={`Cadastros de usuários (últimos ${stats.days} dias)`}
        argumentField="argument"
        legend={false}
        chart={chartProps(data)}
        series={[
          { name: 'Cadastros', key: 'signups', color: '#3d8bfd', type: 'bar' },
        ]}
      />
    )
  }

  renderDedicatedChart(stats) {
    const data = (stats.dedicated_active || []).map(r => ({
      argument: dayLabel(r.date),
      dedicated: Number(r.count) || 0,
    }))
    if (!data.length) return null
    return (
      <TwoAxesChart
        title={`Números exclusivos ativos (últimos ${stats.days} dias)`}
        argumentField="argument"
        legend={false}
        chart={chartProps(data)}
        series={[
          { name: 'Ativos', key: 'dedicated', color: '#00A34A', type: 'line' },
        ]}
      />
    )
  }

  renderMessagesChart(stats) {
    const data = (stats.messages || []).map(r => ({
      argument: dayLabel(r.date),
      inbound: Number(r.inbound) || 0,
      outbound: Number(r.outbound) || 0,
    }))
    if (!data.length) return null
    return (
      <TwoAxesChart
        title={`Mensagens enviadas e recebidas (últimos ${stats.days} dias)`}
        argumentField="argument"
        legend={false}
        chart={chartProps(data)}
        series={[
          { name: 'Recebidas', key: 'inbound', color: '#6f42c1', type: 'line' },
          { name: 'Enviadas', key: 'outbound', color: '#fd7e14', type: 'line' },
        ]}
      />
    )
  }

  renderCapacityChart(capacityChart) {
    if (!capacityChart.length) return null
    return (
      <TwoAxesChart
        title="Conversas ativas × slots (24h)"
        argumentField="argument"
        legend={false}
        chart={chartProps(capacityChart)}
        series={[
          { name: 'Ativas', key: 'active', color: '#3d8bfd', type: 'line' },
          { name: 'Slots', key: 'slots', color: '#3ecf8e', type: 'line' },
        ]}
      />
    )
  }

  render() {
    const { stats, capacityChart, loading, error } = this.state
    const totals = (stats && stats.totals) || {}
    const slots = (stats && stats.slots) || {}
    const utilPct = slots.utilization_percent != null ? `${slots.utilization_percent}%` : '—'
    const surge = slots.surge_multiplier != null ? `× ${slots.surge_multiplier}` : '—'

    const charts = stats ? [
      this.renderSignupChart(stats),
      this.renderDedicatedChart(stats),
      this.renderMessagesChart(stats),
      this.renderCapacityChart(capacityChart),
    ].filter(Boolean) : []

    return (
      <div>
        <ContentHeader title={APP_NAME} small="Painel administrativo" />
        <Content>
          {error ? <p style={{ color: '#c0392b' }}>{error}</p> : null}
          {loading && !stats ? <p style={{ color: '#888' }}>Carregando métricas…</p> : null}

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              marginLeft: -7.5,
              marginRight: -7.5,
            }}
          >
            {[
              {
                icon: 'fa fa-bolt',
                bg: 'warning',
                title: 'Utilização / surge',
                text: `${utilPct} · ${surge}`,
              },
              {
                icon: 'fa fa-users',
                bg: 'info',
                title: 'Usuários (total)',
                text: totals.users != null ? String(totals.users) : '—',
              },
              {
                icon: 'fa fa-user-plus',
                bg: 'info',
                title: 'Cadastros (30d)',
                text: totals.signups_period != null ? String(totals.signups_period) : '—',
              },
              {
                icon: 'fa fa-arrow-down',
                bg: 'primary',
                title: 'Msgs recebidas (30d)',
                text: totals.messages_in_period != null ? String(totals.messages_in_period) : '—',
              },
              {
                icon: 'fa fa-arrow-up',
                bg: 'warning',
                title: 'Msgs enviadas (30d)',
                text: totals.messages_out_period != null ? String(totals.messages_out_period) : '—',
              },
            ].map((card) => (
              <div
                key={card.title}
                style={{
                  flex: '1 1 160px',
                  minWidth: 160,
                  maxWidth: '100%',
                  padding: '0 7.5px',
                  boxSizing: 'border-box',
                  marginBottom: 12,
                }}
              >
                <InfoBox
                  grid={{ cols: '12', style: { width: '100%', marginBottom: 0, float: 'none' } }}
                  icon={card.icon}
                  bg={card.bg}
                  title={card.title}
                  text={card.text}
                />
              </div>
            ))}
          </div>

          {charts.length > 0 ? (
            <div style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap' }}>
              {charts.map((chart, i) => (
                <div key={i} style={chartCellStyle}>
                  {chart}
                </div>
              ))}
            </div>
          ) : null}
        </Content>
      </div>
    )
  }
}
